import { endOfToday } from 'date-fns/endOfToday'
import { format } from 'date-fns/format'
import { fromUnixTime } from 'date-fns/fromUnixTime'
import { getUnixTime } from 'date-fns/getUnixTime'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'

import { TABLE_CONST } from '../constants/Table'
import {
  CategoryGuids,
  CategoryGuidToName,
  DATE_FORMATS_INTL,
  formatDate,
  ORDINAL_DATE_FORMAT,
} from '../constants'
import { COLUMN_NAMES } from '../constants/Transaction'
import { TransactionStatus, TransactionType } from '../types'
import type { Account, Category, Tag, Tagging, Transaction } from '../types'

import { type TransactionFilter } from '../stores/TransactionStore'

import { fromUnixUTC } from './DateUtil'
import { formatCurrency } from './NumberFormatting'

interface AccountsMap {
  [key: string]: Account
}

interface CategoriesMap {
  [key: string]: Category
}

interface TaggingsMap {
  [key: string]: string[]
}

export const buildDetailedTransactions = (
  transactions: Transaction[],
  accounts: Account[],
  categories: Category[],
  taggings: Tagging[] = [],
) => {
  const accountsMap = accounts.reduce((acc: AccountsMap, account: Account) => {
    return { ...acc, [account.guid]: account }
  }, {})

  const categoriesMap = categories.reduce((acc: CategoriesMap, category: Category) => {
    return { ...acc, [category.guid]: category }
  }, {})

  const taggingsMap = taggings.reduce((acc: TaggingsMap, tagging: Tagging) => {
    const { transaction_guid, tag_guid } = tagging

    if (!acc[transaction_guid]) {
      return {
        ...acc,
        [transaction_guid]: [tag_guid],
      }
    }
    const tags = [...acc[transaction_guid], tag_guid] as string[]

    return {
      ...acc,
      [transaction_guid]: tags,
    }
  }, {})

  return transactions.map((transaction: Transaction) => {
    const account = accountsMap[transaction.account_guid]
    const category = transaction.category_guid ? categoriesMap[transaction.category_guid] : null
    const tags = taggingsMap[transaction.guid] ?? []

    return {
      ...transaction,
      account: account?.user_name || 'Unknown',
      accountIsClosed: account?.is_closed,
      accountIsHidden: account?.is_hidden,
      category: category?.name || 'Uncategorized',
      isIncome: transaction.top_level_category_guid === CategoryGuids.INCOME,
      isPending: transaction.feed_status === TransactionStatus.PENDING,
      number: account?.account_number || '',
      payee: transaction.description,
      tags,
    }
  })
}

const UNCATEGORIZED_TERM = 'uncategorized'

export const sortAndFilterTransactions = (
  filter: TransactionFilter,
  expandedSplits: string[],
  sortColumn: string,
  sortDirection: string,
  tags: Tag[],
  transactions: Transaction[],
) => {
  const filteredTransactions = filterTransactions(filter, tags, transactions)

  const sortedTransactions = sortTransactions(filteredTransactions, sortColumn, sortDirection)

  expandedSplits.forEach((guid) => {
    const index = sortedTransactions.findIndex((t) => t.guid === guid)
    const children = transactions.filter((t) => t.parent_guid === guid)
    sortedTransactions.splice(index + 1, 0, ...children)
  })
  return sortedTransactions
}

const filterTransactions = (
  filter: TransactionFilter,
  tags: Tag[],
  transactions: Transaction[],
) => {
  const {
    accounts = [],
    dateRange = { start: startOfMonth(startOfToday()), end: endOfToday() },
    custom = () => true,
    searchTerm: searchFilter = '',
    showSplits = false,
  } = filter

  return (
    transactions
      // Filters out parent of split transactions when showSplits is true and filters out split
      // transaction children when showSplits is false
      .filter((t) => (showSplits ? !t.has_been_split : !t.parent_guid))
      // Filter for selected accounts
      .filter((t) => Boolean(accounts.find((guid) => guid === t.account_guid)))
      // Filter for Date Range.
      // NOTE: bounds are local-midnight (getUnixTime of a local Date) while dates
      // are rendered/exported on the UTC calendar day (fromUnixUTC). For a
      // midnight-UTC row near a month boundary in a negative-offset browser the
      // two can disagree by a day. Left as-is here to keep this fix low-risk;
      // unifying range + search on UTC is tracked with the core-data ISO
      // migration (MEW-3961 follow-up).
      .filter((t) => t.date >= getUnixTime(dateRange.start) && t.date <= getUnixTime(dateRange.end))
      // Custom filter
      .filter(custom)
      // Search filter
      .filter((transaction) => {
        // Don't return uncategorized split transactions
        if (searchFilter === UNCATEGORIZED_TERM && transaction.has_been_split) {
          return false
        }

        // Split check - When searching for "Splits" and transaction is a split or is a parent of a split(s)
        const splitPattern = /^spl/i
        if (
          splitPattern.test(searchFilter) &&
          (transaction.has_been_split || Boolean(transaction.parent_guid))
        ) {
          return true
        }

        // Check transactions tags
        const isTagIncludedInSearch = transaction.tags.some((guid) =>
          isIncludedInSearch(searchFilter, tags.find((tag) => tag.guid === guid)?.name),
        )
        const date = fromUnixTime(transaction.date)
        return (
          isIncludedInSearch(searchFilter, transaction.payee) ||
          isIncludedInSearch(searchFilter, transaction.feed_description) ||
          isIncludedInSearch(searchFilter, transaction.category) ||
          isIncludedInSearch(searchFilter, formatDate(date, DATE_FORMATS_INTL.MONTH)) ||
          isIncludedInSearch(searchFilter, formatDate(date, DATE_FORMATS_INTL.DAY)) ||
          isIncludedInSearch(searchFilter, formatDate(date, DATE_FORMATS_INTL.FULL_MONTH_DAY)) ||
          isIncludedInSearch(searchFilter, formatDate(date, DATE_FORMATS_INTL.MONTH_DAY_YEAR)) ||
          isIncludedInSearch(searchFilter, format(date, ORDINAL_DATE_FORMAT)) ||
          isIncludedInSearch(searchFilter, transaction.account) ||
          isIncludedInSearch(searchFilter, formatCurrency(transaction.amount, '0,0.00')) ||
          isIncludedInSearch(searchFilter, transaction.memo) ||
          isTagIncludedInSearch
        )
      })
  )
}

const sortTransactions = (
  transactions: Transaction[],
  sortColumn: string,
  sortDirection: string,
) => {
  return [...transactions].sort((a: Transaction, b: Transaction) => {
    const column = sortColumn as keyof Transaction
    const sortCompare = (a[column] || '') > (b[column] || '')

    let sortASC = sortCompare ? -1 : 1
    let sortDESC = sortCompare ? 1 : -1

    if (sortColumn === COLUMN_NAMES.DATE || sortColumn === COLUMN_NAMES.AMOUNT) {
      sortASC = sortCompare ? 1 : -1
      sortDESC = sortCompare ? -1 : 1
    }

    return sortDirection === TABLE_CONST.ASC ? sortASC : sortDESC
  })
}

/**
 * Determines if a value is included within the terms set in the search bar
 */
const isIncludedInSearch = (searchFilter: string, value: string | null | undefined) => {
  const lowerCasedTerms = searchFilter.toLowerCase().split(' ')
  const lowerCasedValue = value?.toString().toLowerCase() ?? ''

  return lowerCasedTerms.every((filter) => lowerCasedValue.indexOf(filter) > -1)
}

// TODO: Change the way formatting data is exported in order to handle localization
type FormattedTransaction = {
  Date: string
  Description: string
  'Original Description': string
  Amount: string
  Type: string
  'Parent Category': string
  Category: string
  Account: string
  Tags: string
  Memo: string
  Pending: string
}

/**
 * Collects the GUIDs of all transactions that act as parents
 * to one or more split (child) transactions.
 */
function extractParentTransactionGuids(transactions: Transaction[]): Set<string> {
  return new Set(
    transactions.filter((tx) => tx.parent_guid !== undefined).map((tx) => tx.parent_guid!),
  )
}

/**
 * Organizes child transactions by their parent transaction GUID.
 * Returns a map from parent GUID to an array of its child transactions.
 */
function mapChildTransactionsByParent(transactions: Transaction[]): Record<string, Transaction[]> {
  return transactions.reduce<Record<string, Transaction[]>>((accumulator, tx) => {
    if (tx.parent_guid) {
      if (!accumulator[tx.parent_guid]) accumulator[tx.parent_guid] = []
      accumulator[tx.parent_guid].push(tx)
    }
    return accumulator
  }, {})
}

/**
 * Produces a list of transactions based on the given sorted transactions,
 * where parent transactions that have split children are replaced by those
 * children sorted by descending absolute amount. All other transactions
 * are included as-is, with duplicates removed.
 */
export function flattenTransactionsWithSplits(
  sortedTransactions: Transaction[],
  allTransactions: Transaction[],
): Transaction[] {
  // Set of parent transaction GUIDs that have split child transactions
  const parentTransactionGuidsWithSplits = extractParentTransactionGuids(allTransactions)

  // Map from parent GUID to array of its child split transactions
  const childTransactionsGroupedByParent = mapChildTransactionsByParent(allTransactions)

  // Track GUIDs of transactions already included in the result to avoid duplicates
  const processedTransactionGuids = new Set<string>()

  // Resulting list of transactions with parents replaced by their split children
  const flattenedTransactionList: Transaction[] = []

  for (const transaction of sortedTransactions) {
    if (parentTransactionGuidsWithSplits.has(transaction.guid)) {
      // This transaction is a parent with splits, so replace it with its children
      const splitChildTransactions = childTransactionsGroupedByParent[transaction.guid] ?? []

      splitChildTransactions
        .slice()
        .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
        .forEach((childTransaction) => {
          if (!processedTransactionGuids.has(childTransaction.guid)) {
            flattenedTransactionList.push(childTransaction)
            processedTransactionGuids.add(childTransaction.guid)
          }
        })
    } else if (!processedTransactionGuids.has(transaction.guid)) {
      // Normal transaction (no splits), add as-is if not already added
      flattenedTransactionList.push(transaction)
      processedTransactionGuids.add(transaction.guid)
    }
  }

  return flattenedTransactionList
}

export const exportTransactionsToCSV = (transactions: Transaction[], tags: Tag[]) => {
  const csvContent = structureCSVData(formatTransactions(transactions, tags))

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.setAttribute('download', 'transactions.csv')

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(link.href)
}

/**
 * Formats a list of transactions into a list that will be used when
 * downloaded to a CSV file.
 */
const formatTransactions = (transactions: Transaction[], tags: Tag[]) => {
  return transactions.map((transaction) => ({
    // Post dates are calendar days; format on the UTC day so the export can't
    // shift to the prior day in a Western-offset browser.
    Date: formatDate(fromUnixUTC(transaction.date), DATE_FORMATS_INTL.YEAR_MONTH_DAY),
    Description: getDescription(transaction),
    'Original Description': transaction.feed_description,
    Amount: `${transaction.amount}`,
    Type: transaction.transaction_type === TransactionType.CREDIT ? 'Credit' : 'Debit',
    'Parent Category': CategoryGuidToName[transaction.top_level_category_guid || ''],
    Category: transaction.category,
    Account: transaction.account,
    Tags: getTags(transaction, tags),
    Memo: transaction.memo,
    Pending: transaction.isPending ? 'TRUE' : 'FALSE',
  })) as FormattedTransaction[]
}

/**
 * Escapes a value for safe CSV output
 */
const escapeCsvField = (field?: string | null): string => {
  if (!field) return ''
  return /[,"\n\r]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field
}

/**
 * Structures the CSV file data using the formatted transactions
 */
const structureCSVData = (formattedTransactions: FormattedTransaction[]) => {
  // Guard the empty export: Object.keys(undefined) would throw for a zero-row
  // list (e.g. an over-filtered range), so emit an empty file instead.
  if (formattedTransactions.length === 0) return ''

  const csvHeader = Object.keys(formattedTransactions[0]).join(',').concat('\r\n')
  const csvRows = formattedTransactions
    .map((transaction) => Object.values(transaction).map(escapeCsvField).join(','))
    .join('\r\n')

  return csvHeader + csvRows
}

/**
 * Get the description for a transaction that will be used within the CSV copy.
 * We want to highlight that split transactions are a split as well as
 * hidden transactions are hidden.
 */
const getDescription = (transaction: Transaction) => {
  let description = transaction.parent_guid ? 'Split: ' : ''
  description += transaction.is_hidden ? '(Excluded) ' : ''
  description += transaction.description

  return description
}

const getTags = (transaction: Transaction, tags: Tag[]) => {
  return transaction.tags
    .map((tagGuid) => {
      const tag = tags.filter((t) => t.guid === tagGuid)[0]

      return tag?.name ?? ''
    })
    .join(' ')
}
